/**
 * Idle-session auto-logout for AIFLedger.
 *
 * • Tracks user activity via mousemove / keydown / click / scroll / touchstart.
 * • After IDLE_TIMEOUT_MS with no activity → logout and redirect to home ("/").
 * • Shows a warning toast IDLE_WARN_LEAD_MS before logout with a
 *   "Stay signed in" button that resets the timer.
 * • Multi-tab aware: writes lastActivity to localStorage so any tab's activity
 *   counts across all open tabs.
 * • Only runs when a user is signed in.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";

// 15 minutes idle → logout by default. Overridable by the server-side security
// policy exposed on `user.session_idle_minutes`.
const DEFAULT_IDLE_MINUTES = 15;
const IDLE_WARN_LEAD_MS = 60 * 1000;
const ACTIVITY_THROTTLE_MS = 5 * 1000;
const CHECK_INTERVAL_MS = 5 * 1000;
const STORAGE_KEY = "aif_last_activity";

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "click", "scroll", "touchstart"];

export function useIdleLogout(user, logout) {
  const lastWriteRef = useRef(0);
  const warnedRef = useRef(false);
  const warnToastIdRef = useRef(null);

  useEffect(() => {
    if (!user) {
      warnedRef.current = false;
      warnToastIdRef.current = null;
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      return undefined;
    }

    const bump = () => {
      const now = Date.now();
      if (now - lastWriteRef.current < ACTIVITY_THROTTLE_MS) return;
      lastWriteRef.current = now;
      try { localStorage.setItem(STORAGE_KEY, String(now)); } catch { /* ignore */ }
      // If the warning is showing but the user became active, dismiss it.
      if (warnedRef.current) {
        warnedRef.current = false;
        if (warnToastIdRef.current) {
          toast.dismiss(warnToastIdRef.current);
          warnToastIdRef.current = null;
        }
      }
    };

    // Seed
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
    lastWriteRef.current = Date.now();

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    const idleTimeoutMs = (Number(user.session_idle_minutes) || DEFAULT_IDLE_MINUTES) * 60 * 1000;

    const doLogout = async () => {
      try { await logout(); } catch { /* ignore */ }
      toast.error("Signed out due to inactivity.");
      // Redirect to home page (not /login) per product spec.
      window.location.href = "/";
    };

    const check = () => {
      let last;
      try { last = Number(localStorage.getItem(STORAGE_KEY)) || Date.now(); }
      catch { last = Date.now(); }
      const idle = Date.now() - last;

      if (idle >= idleTimeoutMs) {
        doLogout();
        return;
      }
      if (idle >= idleTimeoutMs - IDLE_WARN_LEAD_MS && !warnedRef.current) {
        warnedRef.current = true;
        warnToastIdRef.current = toast.warning(
          "You'll be signed out in 60 seconds due to inactivity.",
          {
            duration: IDLE_WARN_LEAD_MS,
            action: {
              label: "Stay signed in",
              onClick: () => {
                bump();
                warnedRef.current = false;
                if (warnToastIdRef.current) {
                  toast.dismiss(warnToastIdRef.current);
                  warnToastIdRef.current = null;
                }
              },
            },
          }
        );
      }
    };

    const interval = setInterval(check, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(interval);
    };
  }, [user, logout]);
}
